import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, Clock, Gem, Info, CreditCard, Phone, User } from 'lucide-react'
import toast from 'react-hot-toast'
import useRubWithdrawStore from '../store/useRubWithdrawStore'
import {
  rubToCrystals, parsePhone, formatPhone, isValidPhone, formatCard, isValidCard, normalizeName, isValidName,
} from '../services/rubWithdrawApi'
import SbpBankSelect from './SbpBankSelect'
import useAppStore from '../../../store/useAppStore'
import { transactionApi } from '../../../services'

const formatNumber = (value) => Number(value).toLocaleString('en-US')

const NO_BANKS = []

const INPUT_CLASS = 'w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50'

/**
 * RubWithdraw — вывод кристаллов в рублях одним из способов: СБП (method="sbp") или карта РФ (method="card").
 * Игрок вводит целую сумму к получению в рублях, экран считает кристаллы по курсу из API — так же, как бэкенд.
 * Данные экрана загружает страница вывода (useRubWithdrawStore): от них зависит, какие способы видны.
 */
const RubWithdraw = ({ method }) => {
  const isSbp = method === 'sbp'
  const { t } = useTranslation()
  const { balance, loadProfile } = useAppStore()
  const { info, history, isLoading, isSubmitting, loadData, submitRubWithdraw } = useRubWithdrawStore()

  const [phone, setPhone] = useState('')
  const [bankId, setBankId] = useState('')
  const [card, setCard] = useState('')
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  // Новая сумма после 409 rate changed — ждёт явного подтверждения игрока
  const [confirmCrystals, setConfirmCrystals] = useState(null)
  const prefilled = useRef(false)

  const methodInfo = info?.methods?.[method]
  const banks = info?.sbp_banks || NO_BANKS

  // Предзаполнение формы: минимальная сумма и реквизиты последней заявки этим способом
  useEffect(() => {
    if (!methodInfo || prefilled.current) return
    prefilled.current = true
    setAmount(String(methodInfo.min_rub))
    const last = methodInfo.last
    if (!last) return
    setName(last.name || '')
    if (isSbp) {
      setPhone(parsePhone(last.phone))
      if (banks.some((bank) => bank.id === last.bank_id)) setBankId(last.bank_id)
    } else {
      setCard(formatCard(last.card))
    }
  }, [methodInfo, banks, isSbp])

  const statusLabels = {
    new: t('rubWithdraw.status.new'),
    approved: t('rubWithdraw.status.approved'),
    success: t('rubWithdraw.status.success'),
    rejected: t('rubWithdraw.status.rejected'),
    cancelled: t('rubWithdraw.status.cancelled'),
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case 'new':
        return { icon: Clock, iconClass: 'text-blue-400', textClass: 'text-blue-400', containerClass: 'border-blue-500/20 bg-blue-500/5' }
      case 'approved':
        return { icon: Loader2, iconClass: 'text-blue-400 animate-spin', textClass: 'text-blue-400', containerClass: 'border-blue-500/20 bg-blue-500/5' }
      case 'success':
        return { icon: CheckCircle2, iconClass: 'text-accent-primary', textClass: 'text-accent-primary', containerClass: 'border-accent-primary/20 bg-accent-primary/5' }
      case 'rejected':
      case 'cancelled':
        return { icon: XCircle, iconClass: 'text-red-400', textClass: 'text-red-400', containerClass: 'border-red-500/20 bg-red-500/5' }
      default:
        return { icon: Clock, iconClass: 'text-text-secondary', textClass: 'text-text-secondary', containerClass: 'border-white/10 bg-surface' }
    }
  }

  if (isLoading && !info) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  if (!info || !methodInfo?.enabled) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
        <p className="text-text-secondary mb-4">
          {info ? t('rubWithdraw.errors.methodDisabled') : t('rubWithdraw.errors.loadFailed')}
        </p>
        <button
          type="button"
          onClick={() => loadData().catch(() => {})}
          className="px-4 py-2 rounded-lg border border-white/10 text-sm text-white hover:border-accent-primary/40 transition-colors"
        >
          {t('rubWithdraw.retry')}
        </button>
      </div>
    )
  }

  // Расчёт суммы и проверки формы
  const rate = info.crystals_per_1000_rub_net
  const amountNum = Number(amount)
  const amountValid = amount !== '' && Number.isSafeInteger(amountNum) && amountNum >= methodInfo.min_rub && amountNum <= methodInfo.max_rub
  const crystals = amountValid ? rubToCrystals(amountNum, rate) : null
  const phoneValid = isValidPhone(phone)
  const cardValid = isValidCard(card)
  const nameValid = isValidName(name)
  const requisitesValid = isSbp ? phoneValid && Boolean(bankId) : cardValid
  const notEnough = crystals !== null && crystals > balance
  const isBlocked = !info.can_withdraw || Boolean(info.active) || !rate
  const isConfirming = confirmCrystals !== null && confirmCrystals === crystals
  const canSubmit = !isBlocked && !isSubmitting && amountValid && requisitesValid && nameValid && crystals !== null && !notEnough
  const limits = { min: formatNumber(methodInfo.min_rub), max: formatNumber(methodInfo.max_rub) }

  const reasonMessages = {
    'withdraw banned': t('rubWithdraw.errors.withdrawBanned'),
    'active request': t('rubWithdraw.errors.activeRequest'),
    'service unavailable': t('rubWithdraw.errors.serviceUnavailable'),
  }
  const blockedReason = !info.can_withdraw || !rate
    ? reasonMessages[info.reason] || reasonMessages['service unavailable']
    : null

  // Любая правка формы сбрасывает ожидание подтверждения нового курса
  const edit = (setter) => (value) => {
    setter(value)
    setConfirmCrystals(null)
  }

  const submit = async () => {
    if (!canSubmit) return
    const payload = isSbp
      ? { method, amount: amountNum, crystals, phone: `+7${phone}`, bank_id: bankId, name: normalizeName(name) }
      : { method, amount: amountNum, crystals, card: card.replace(/\D/g, ''), name: normalizeName(name) }
    try {
      await submitRubWithdraw(payload)
      setConfirmCrystals(null)
      toast.success(t('rubWithdraw.success'))
      // Кристаллы списаны сразу — обновляем баланс
      loadProfile().catch(() => {})
    } catch (err) {
      setConfirmCrystals(null)
      // 429 и сетевые ошибки уже показаны общим API-клиентом
      if (err?.isNotified || err?.isHandledAuthError) return

      if (err.message === 'rate changed' && err.data?.crystals) {
        setConfirmCrystals(err.data.crystals)
        return
      }

      const errorMap = {
        'disabled': t('rubWithdraw.errors.disabled'),
        'deposit required': t('rubWithdraw.errors.disabled'),
        'withdraw banned': t('rubWithdraw.errors.withdrawBanned'),
        'bad method': t('rubWithdraw.errors.badMethod'),
        'method disabled': t('rubWithdraw.errors.methodDisabled'),
        'bad amount': t('rubWithdraw.errors.badAmount', limits),
        'bad phone': t('rubWithdraw.errors.badPhone'),
        'bad bank': t('rubWithdraw.errors.badBank'),
        'bad card': t('rubWithdraw.errors.badCard'),
        'bad name': t('rubWithdraw.errors.badName'),
        'card daily limit': t('rubWithdraw.errors.cardDailyLimit'),
        'card monthly limit': t('rubWithdraw.errors.cardMonthlyLimit'),
        'requisites monthly limit': t('rubWithdraw.errors.requisitesMonthlyLimit'),
        'active request': t('rubWithdraw.errors.activeRequest'),
        'insufficient balance': t('rubWithdraw.errors.insufficientBalance'),
        'welcome lock': t('rubWithdraw.errors.welcomeLock'),
        'service unavailable': t('rubWithdraw.errors.serviceUnavailable'),
        'rate changed': t('rubWithdraw.errors.serviceUnavailable'),
      }
      toast.error(errorMap[err.message] || t('rubWithdraw.errors.errorBackend'))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    submit()
  }

  const active = info.active
  const methodHistory = history.filter((item) => item.method === method)
  const fieldDisabled = isBlocked || isSubmitting

  return (
    <div className="space-y-6">
      {/* Активная заявка (любым способом — она блокирует новые) */}
      {active && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={20} className="text-blue-400 animate-spin" />
            <span className="font-semibold text-blue-400">
              {t('rubWithdraw.activeRequest')} <bdi dir="ltr">#{active.id}</bdi>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-text-secondary">
              {t(`rubWithdraw.methods.${active.method}`, active.method)} · <bdi dir="ltr">{active.requisites}</bdi>
            </span>
            <bdi dir="ltr" className="flex-shrink-0 font-bold text-white">{formatNumber(active.amount_rub)} ₽</bdi>
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs text-text-secondary">
            <bdi dir="ltr">{formatNumber(active.crystals)}</bdi> <Gem size={12} className="text-accent-primary" />
          </div>
          <p className="text-xs text-blue-400 mt-2">{statusLabels[active.status] || active.status}</p>
          <p className="text-xs text-text-secondary mt-1">{t('rubWithdraw.activeRequestDesc')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {isSbp ? (
          <>
            {/* Телефон получателя */}
            <div>
              <label htmlFor="rub-withdraw-phone" className="block text-sm font-medium text-text-secondary mb-2">
                {t('rubWithdraw.phoneLabel')}
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
                <input
                  id="rub-withdraw-phone"
                  type="tel"
                  dir="ltr"
                  inputMode="tel"
                  value={formatPhone(phone)}
                  onChange={(e) => edit(setPhone)(parsePhone(e.target.value))}
                  disabled={fieldDisabled}
                  placeholder="+7 (900) 000-00-00"
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </div>
              {phone.length === 10 && !phoneValid && (
                <p className="text-xs text-red-400 mt-1.5">{t('rubWithdraw.errors.badPhone')}</p>
              )}
            </div>

            {/* Банк СБП */}
            <div>
              <label htmlFor="rub-withdraw-bank" className="block text-sm font-medium text-text-secondary mb-2">
                {t('rubWithdraw.bankLabel')}
              </label>
              <SbpBankSelect id="rub-withdraw-bank" banks={banks} value={bankId} onChange={edit(setBankId)} disabled={fieldDisabled} />
              <p className="text-xs text-text-secondary mt-1.5">{t('rubWithdraw.bankHint')}</p>
            </div>
          </>
        ) : (
          /* Номер карты */
          <div>
            <label htmlFor="rub-withdraw-card" className="block text-sm font-medium text-text-secondary mb-2">
              {t('rubWithdraw.cardLabel')}
            </label>
            <div className="relative">
              <CreditCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
              <input
                id="rub-withdraw-card"
                type="text"
                dir="ltr"
                inputMode="numeric"
                value={card}
                onChange={(e) => edit(setCard)(formatCard(e.target.value))}
                disabled={fieldDisabled}
                placeholder="0000 0000 0000 0000"
                autoComplete="off"
                className={INPUT_CLASS}
              />
            </div>
            {card.replace(/\D/g, '').length >= 16 && !cardValid && (
              <p className="text-xs text-red-400 mt-1.5">{t('rubWithdraw.errors.badCard')}</p>
            )}
          </div>
        )}

        {/* ФИО получателя */}
        <div>
          <label htmlFor="rub-withdraw-name" className="block text-sm font-medium text-text-secondary mb-2">
            {t('rubWithdraw.nameLabel')}
          </label>
          <div className="relative">
            <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              id="rub-withdraw-name"
              type="text"
              dir="ltr"
              value={name}
              onChange={(e) => edit(setName)(e.target.value)}
              disabled={fieldDisabled}
              // Пример всегда кириллицей — банк принимает ФИО только так, независимо от языка интерфейса
              placeholder="Иванов Иван Иванович"
              autoComplete="off"
              maxLength={100}
              className={INPUT_CLASS}
            />
          </div>
          <p className={`text-xs mt-1.5 ${name.trim() !== '' && !nameValid ? 'text-red-400' : 'text-text-secondary'}`}>
            {name.trim() !== '' && !nameValid ? t('rubWithdraw.errors.badName') : t('rubWithdraw.nameHint')}
          </p>
        </div>

        {/* Сумма к получению в рублях */}
        <div>
          <label htmlFor="rub-withdraw-amount" className="block text-sm font-medium text-text-secondary mb-2">
            {t('rubWithdraw.amountLabel')}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] text-center font-bold text-accent-primary">₽</span>
            <input
              id="rub-withdraw-amount"
              type="text"
              dir="ltr"
              inputMode="numeric"
              value={amount ? formatNumber(amount) : ''}
              onChange={(e) => edit(setAmount)(e.target.value.replace(/\D/g, '').slice(0, 7))}
              disabled={fieldDisabled}
              placeholder={formatNumber(methodInfo.min_rub)}
              className={INPUT_CLASS}
            />
          </div>
          <p className={`text-xs mt-1.5 ${amount !== '' && !amountValid ? 'text-red-400' : 'text-text-secondary'}`}>
            {t('rubWithdraw.limits', limits)}
          </p>
        </div>

        {/* Сумма к списанию */}
        {crystals !== null && (
          <div className="rounded-lg bg-accent-primary/10 border border-accent-primary/20 p-4 text-center">
            <p className="text-sm text-text-secondary mb-1">{t('rubWithdraw.willDebit')}</p>
            <p className="text-3xl font-bold text-accent-primary inline-flex items-center gap-2">
              <bdi dir="ltr">{formatNumber(crystals)}</bdi> <Gem size={24} className="text-accent-primary" />
            </p>
            <p className="text-xs text-text-secondary mt-1 hidden">
              {t('rubWithdraw.commission', { percent: info.commission_percent })}
            </p>
          </div>
        )}

        {/* Причина блокировки формы */}
        {blockedReason && (
          <div className="flex items-center gap-2 text-sm text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{blockedReason}</span>
          </div>
        )}

        {isConfirming ? (
          // Подтверждение новой суммы после обновления курса
          <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-4 space-y-3">
            <p className="text-sm text-yellow-400">
              {t('rubWithdraw.rateChanged', { crystals: formatNumber(confirmCrystals) })}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setConfirmCrystals(null)}
                disabled={isSubmitting}
                className="py-3 rounded-lg border border-white/10 text-white font-semibold hover:border-white/30 transition-colors disabled:opacity-50"
              >
                {t('rubWithdraw.cancel')}
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && <Loader2 size={18} className="animate-spin" />}
                {t('rubWithdraw.confirm')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t('rubWithdraw.submitting')}
              </>
            ) : notEnough ? (
              t('rubWithdraw.errors.insufficientBalance')
            ) : (
              t('rubWithdraw.submit')
            )}
          </button>
        )}

        <div className="flex items-start gap-2 text-xs text-text-secondary hidden">
          <Info size={14} className="flex-shrink-0 mt-0.5 text-accent-primary" />
          <span>{t('rubWithdraw.note')}</span>
        </div>
      </form>

      {/* История заявок этим способом */}
      {methodHistory.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">{t('rubWithdraw.historyTitle')}</h3>
          <div className="space-y-2">
            {methodHistory.map((item) => {
              const statusConfig = getStatusConfig(item.status)
              const StatusIcon = statusConfig.icon
              return (
                <div key={item.id} className={`rounded-lg border p-3 ${statusConfig.containerClass}`}>
                  <div className="flex items-center gap-3">
                    <StatusIcon size={18} className={`flex-shrink-0 ${statusConfig.iconClass}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <bdi dir="ltr" className="text-sm font-medium text-white">{formatNumber(item.amount_rub)} ₽</bdi>
                        <bdi dir="ltr" className="text-xs text-text-secondary">#{item.id}</bdi>
                      </div>
                      <div className="mt-0.5 text-xs text-text-secondary truncate">
                        <bdi dir="ltr">{item.requisites}</bdi>
                        {item.bank_name && <> · {item.bank_name}</>}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5 text-xs text-text-secondary">
                        <span className="inline-flex items-center gap-1">
                          <bdi dir="ltr">{formatNumber(item.crystals)}</bdi> <Gem size={12} className="text-accent-primary" />
                        </span>
                        <span>{transactionApi.formatTimeAgo(item.created_sec_ago, t)}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`mt-2 pt-2 border-t border-white border-opacity-5 text-xs font-medium ${statusConfig.textClass}`}>
                    {statusLabels[item.status] || item.status}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default RubWithdraw
